import {
	getFinalSelectionCount,
	getTableSelectedIds,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { DataMipPaymentRes, TransactionState } from '@dealstack/shared/lib/generated';
import { Table } from '@tanstack/react-table';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export interface BulkUpdatePaymentParams {
	ids: string[];
	status: TransactionState;
	annotation?: string;
}

export const createBulkActions = (
	table: Table<DataMipPaymentRes>,
	{
		isBusy,
		canBulkUpdate,
		isDownloading,
		tMipManage,
		onDownload,
		onBulkUpdate,
		onOpenIncompleteDrawer,
	}: {
		isBusy: boolean;
		canBulkUpdate: boolean;
		isDownloading: boolean;
		tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
		onDownload: () => void;
		onBulkUpdate: (params: BulkUpdatePaymentParams) => void;
		onOpenIncompleteDrawer: (
			paymentIds: string[],
			annotation?: string | null,
			table?: Table<DataMipPaymentRes>
		) => void;
	}
): BulkActionGroup<DataMipPaymentRes> => {
	const primary: BulkAction<DataMipPaymentRes>[] = [];
	const secondary: BulkAction<DataMipPaymentRes>[] = [
		{
			key: 'download_payments',
			label: tMipManage('payments.bulk_actions.download'),
			disabled: isDownloading,
			executeImmediately: true,
			onExecute: onDownload,
		},
	];

	if (!canBulkUpdate) {
		return { secondary };
	}

	const getSelectedIds = () => getTableSelectedIds(table);
	const getButtonLabel = (label: string) => `${label} (${getFinalSelectionCount(table)})`;

	primary.push(
		{
			key: 'bulk_complete',
			label: tMipManage('payments.bulk_actions.mark_as_completed'),
			buttonLabel: () =>
				getButtonLabel(tMipManage('payments.bulk_actions.mark_as_completed')),
			disabled: isBusy,
			onExecute: () =>
				onBulkUpdate({
					ids: getSelectedIds(),
					status: TransactionState.COMPLETED,
				}),
		},
		{
			key: 'bulk_reject',
			label: tMipManage('payments.bulk_actions.reject'),
			buttonLabel: () => getButtonLabel(tMipManage('payments.bulk_actions.reject')),
			disabled: isBusy,
			onExecute: () => {
				const selectedIds = getSelectedIds();

				if (!selectedIds.length) {
					return;
				}

				onOpenIncompleteDrawer(selectedIds, undefined, table);
			},
		},
		{
			key: 'bulk_pending',
			label: tMipManage('payments.bulk_actions.mark_as_pending'),
			buttonLabel: () => getButtonLabel(tMipManage('payments.bulk_actions.mark_as_pending')),
			disabled: isBusy,
			onExecute: () =>
				onBulkUpdate({
					ids: getSelectedIds(),
					status: TransactionState.PENDING,
				}),
		}
	);

	return { primary, secondary };
};
